/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import { clearOldSession, loginUserViaSessionData } from './_shared';

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;
const IDP_SESSION_CALL_TIMEOUT = 20000;
const currentRoot = window.location.origin;
const urlParams = new URLSearchParams(window.location.search);

const API_ROUTES = {
  identityProviderSessions: idpName => `${API_BASE_URL}/auth/sso/idps/${idpName}/sessions`,
  identityProvider: idpName => `${API_BASE_URL}/auth/sso/idps/${idpName}`,
};

const FRONT_END_ROUTES = {
  ssoLoginUrl: idpName => `${currentRoot}/${idpName}/auth/sso/login`,
  afterLoginRedirectUrl: shuttleRoot => `/${shuttleRoot}/page/home`,
};

const config = {};
let _abortController;

const cancelCurrentRequest = function cancelCurrentRequestFn() {
  if (_abortController) {
    _abortController.abort();
  }
};

const _loadIdpConfig = async function _loadIdpConfigFn(idpName) {
  _abortController = new AbortController();
  try {
    const { data } = await axios.get(API_ROUTES.identityProvider(idpName), {
      signal: _abortController.signal,
    });
    return data;
  } catch (error) {
    if (axios.isCancel(error)) {
      return null;
    }
    throw error;
  }
};

const _buildUrlWithParams = function buildUrlWithParamsFn(urlString, params = {}) {
  const url = new URL(urlString);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  return url.href;
};

const init = async function initFn(idpName) {
  const idpConfig = await _loadIdpConfig(idpName);

  Object.assign(config, {
    ...idpConfig,
    name: idpName,
  });
  return Boolean(idpConfig);
};

const buildAuthorizeUrlForIdp = function buildAuthorizeUrlForIdpFn(customUrlParams = {}) {
  const stateParam = urlParams.get('state');

  const defaultParams = {
    client_id: config.client_id,
    scope: config.authorize_scopes,
    response_type: 'code',
    redirect_uri: FRONT_END_ROUTES.ssoLoginUrl(config.name),
    state: stateParam,
  };
  const authorizeUrlParams = { ...defaultParams, ...customUrlParams };

  return moduleExports._buildUrlWithParams(config.authorize_url, authorizeUrlParams);
};

const _createSessionFromParameters = async function _createSessionFromParametersFn(parameters = {}) {
  await clearOldSession();

  const { data: loggedInData } = await axios.post(
    API_ROUTES.identityProviderSessions(config.name),
    {
      ...parameters,
      redirectUrl: FRONT_END_ROUTES.ssoLoginUrl(config.name),
    },
    { timeout: IDP_SESSION_CALL_TIMEOUT },
  );

  await loginUserViaSessionData(loggedInData);

  return loggedInData;
};

const tryLoginWithIdpCode = async function tryLoginWithIdpCodeFn() {
  const codeParam = urlParams.get('code');
  const stateParam = urlParams.get('state');

  if (!codeParam || !stateParam) {
    throw new Error('Both code and state parameters are required!');
  }

  // State format: <idpPlanId>-<idpClientId> (round-tripped through the IdP)
  const [idpPlanId, idpClientId] = stateParam.split('-');

  const { session, username, meta } = await _createSessionFromParameters({ code: codeParam, idpPlanId, idpClientId });
  const redirectToPath = FRONT_END_ROUTES.afterLoginRedirectUrl(meta.shuttle_root);

  return { session, username, redirectToPath };
};

const moduleExports = {
  init,
  buildAuthorizeUrlForIdp,
  tryLoginWithIdpCode,
  cancelCurrentRequest,
  _buildUrlWithParams,
  _createSessionFromParameters,
  _loadIdpConfig,
};

export default moduleExports;
